import {Badge, Box, Divider, Group, Modal, Stack, Text} from '@mantine/core'
import {Fragment} from 'react'

import type {ProspectDetail} from 'src/api/client/models/prospect'
import {engagementBadgeColor} from 'src/lib/engagement-colors'
import {formatCurrency} from 'src/lib/format'

type ProspectDetailModalProps = {
    prospect: ProspectDetail
    opened: boolean
    onClose: () => void
}

function Section({title, children}: {title: string; children: React.ReactNode}) {
    return (
        <Stack gap="xs">
            <Text size="sm" fw={600}>
                {title}
            </Text>
            {children}
        </Stack>
    )
}

function DetailRow({label, value}: {label: string; value: React.ReactNode}) {
    return (
        <Group gap="sm" align="flex-start" wrap="nowrap">
            <Text size="sm" w={180} style={{flexShrink: 0}}>
                {label}:
            </Text>
            <Box style={{flex: 1, textAlign: 'right'}}>
                {typeof value === 'string' ? (
                    <Text size="sm" style={{textAlign: 'right'}}>
                        {value}
                    </Text>
                ) : (
                    value
                )}
            </Box>
        </Group>
    )
}

function formatDate(dateString: string | null): string {
    if (!dateString) return '—'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })
}

function formatBoolean(value: boolean | null): string {
    if (value === null) return '—'
    return value ? 'Yes' : 'No'
}

function formatCurrencyOrDash(value: string | null): string {
    if (!value || value === '0' || value === '0.00' || Number(value) === 0) {
        return '—'
    }
    return formatCurrency(value)
}

function hasNonEmptyString(value: string | null | undefined): boolean {
    return Boolean(value?.trim())
}

function hasCurrencyValue(value: string | null | undefined): boolean {
    if (!value?.trim()) return false
    if (value === '0' || value === '0.00' || Number(value) === 0) return false
    return true
}

function hasAddressContent(prospect: ProspectDetail): boolean {
    return (
        hasNonEmptyString(prospect.addressLine1) ||
        hasNonEmptyString(prospect.addressLine2) ||
        hasNonEmptyString(prospect.city) ||
        hasNonEmptyString(prospect.state) ||
        hasNonEmptyString(prospect.zipCode)
    )
}

export function ProspectDetailModal({prospect, opened, onClose}: ProspectDetailModalProps) {
    const fullName = [prospect.firstName, prospect.middleName, prospect.lastName]
        .filter(Boolean)
        .join(' ')

    const showEngagement = Boolean(prospect.engagementLevel)
    const showCapacity = hasNonEmptyString(prospect.capacityBucket)
    const showManager = hasNonEmptyString(prospect.managerName)
    const showOverview = showEngagement || showCapacity || showManager

    const showSourceId = hasNonEmptyString(prospect.sourceUniqueId)
    const showContact = hasAddressContent(prospect) || showSourceId

    const showRealEstate = hasCurrencyValue(prospect.realEstateEst)
    const showSecurities = hasCurrencyValue(prospect.secStockValue)
    const showBusinessRevenue = hasCurrencyValue(prospect.businessRevenue)
    const showBusinessAffiliation = hasNonEmptyString(prospect.businessAffiliation)
    const showFoundation = hasNonEmptyString(prospect.foundation)
    const showMedianIncome = prospect.medianHouseholdIncome != null

    const showTotalGifts = hasCurrencyValue(prospect.totalGiftAmount)
    const showNumGifts = prospect.numGifts != null
    const showTotalCharitable = hasCurrencyValue(prospect.totalCharitableGiving)
    const showTotalPolitical = hasCurrencyValue(prospect.totalPoliticalGiving)
    const showLargestGift = hasCurrencyValue(prospect.largestGiftAmount)
    const showLargestGiftDate = Boolean(prospect.largestGiftDate)
    const showFirstGift = hasCurrencyValue(prospect.firstGiftAmount)
    const showFirstGiftDate = Boolean(prospect.firstDateRange)
    const showLastGiftDate = Boolean(prospect.lastGiftDate)
    const showGiftHistorySection =
        showTotalGifts ||
        showNumGifts ||
        showTotalCharitable ||
        showTotalPolitical ||
        showLargestGift ||
        showLargestGiftDate ||
        showFirstGift ||
        showFirstGiftDate ||
        showLastGiftDate

    type SectionEntry = {key: string; node: React.ReactNode}
    const sectionEntries: SectionEntry[] = []
    if (showOverview) {
        sectionEntries.push({
            key: 'overview',
            node: (
                <Section title="Overview">
                    {showEngagement && (
                        <DetailRow
                            label="Engagement"
                            value={
                                <Badge
                                    color={engagementBadgeColor(prospect.engagementLevel)}
                                    size="sm"
                                >
                                    {prospect.engagementLevel}
                                </Badge>
                            }
                        />
                    )}
                    {showCapacity && <DetailRow label="Capacity" value={prospect.capacityBucket} />}
                    {showManager && <DetailRow label="Manager" value={prospect.managerName} />}
                </Section>
            ),
        })
    }
    if (showContact) {
        sectionEntries.push({
            key: 'contact',
            node: (
                <Section title="Contact Information">
                    {hasAddressContent(prospect) && (
                        <DetailRow
                            label="Address"
                            value={
                                <Stack gap={0}>
                                    {hasNonEmptyString(prospect.addressLine1) && (
                                        <Text size="sm">{prospect.addressLine1}</Text>
                                    )}
                                    {hasNonEmptyString(prospect.addressLine2) && (
                                        <Text size="sm">{prospect.addressLine2}</Text>
                                    )}
                                    {[prospect.city, prospect.state, prospect.zipCode].filter(
                                        Boolean,
                                    ).length > 0 && (
                                        <Text size="sm">
                                            {[prospect.city, prospect.state, prospect.zipCode]
                                                .filter(Boolean)
                                                .join(', ')}
                                        </Text>
                                    )}
                                </Stack>
                            }
                        />
                    )}
                    {showSourceId && (
                        <DetailRow label="Source ID" value={prospect.sourceUniqueId} />
                    )}
                </Section>
            ),
        })
    }
    sectionEntries.push({
        key: 'wealth',
        node: (
            <Section title="Wealth Indicators">
                {showRealEstate && (
                    <DetailRow
                        label="Real Estate Value"
                        value={formatCurrencyOrDash(prospect.realEstateEst)}
                    />
                )}
                {showSecurities && (
                    <DetailRow
                        label="Securities Value"
                        value={formatCurrencyOrDash(prospect.secStockValue)}
                    />
                )}
                <DetailRow
                    label="Stock/Insider Status"
                    value={formatBoolean(prospect.secStockOrInsider)}
                />
                {showBusinessRevenue && (
                    <DetailRow
                        label="Business Revenue"
                        value={formatCurrencyOrDash(prospect.businessRevenue)}
                    />
                )}
                {showBusinessAffiliation && (
                    <DetailRow label="Business Affiliation" value={prospect.businessAffiliation} />
                )}
                {showFoundation && <DetailRow label="Foundation" value={prospect.foundation} />}
                {showMedianIncome && (
                    <DetailRow
                        label="Median Household Income"
                        value={formatCurrency(String(prospect.medianHouseholdIncome))}
                    />
                )}
            </Section>
        ),
    })
    if (showGiftHistorySection) {
        sectionEntries.push({
            key: 'gift',
            node: (
                <Section title="Gift History">
                    {showTotalGifts && (
                        <DetailRow
                            label="Total Gifts"
                            value={formatCurrencyOrDash(prospect.totalGiftAmount)}
                        />
                    )}
                    {showNumGifts && (
                        <DetailRow label="Number of Gifts" value={String(prospect.numGifts)} />
                    )}
                    {showTotalCharitable && (
                        <DetailRow
                            label="Total Charitable Giving"
                            value={formatCurrencyOrDash(prospect.totalCharitableGiving)}
                        />
                    )}
                    {showTotalPolitical && (
                        <DetailRow
                            label="Total Political Giving"
                            value={formatCurrencyOrDash(prospect.totalPoliticalGiving)}
                        />
                    )}
                    {showLargestGift && (
                        <DetailRow
                            label="Largest Gift"
                            value={formatCurrencyOrDash(prospect.largestGiftAmount)}
                        />
                    )}
                    {showLargestGiftDate && (
                        <DetailRow
                            label="Largest Gift Date"
                            value={formatDate(prospect.largestGiftDate)}
                        />
                    )}
                    {showFirstGift && (
                        <DetailRow
                            label="First Gift"
                            value={formatCurrencyOrDash(prospect.firstGiftAmount)}
                        />
                    )}
                    {showFirstGiftDate && (
                        <DetailRow
                            label="First Gift Date"
                            value={formatDate(prospect.firstDateRange)}
                        />
                    )}
                    {showLastGiftDate && (
                        <DetailRow
                            label="Last Gift Date"
                            value={formatDate(prospect.lastGiftDate)}
                        />
                    )}
                </Section>
            ),
        })
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size="md"
            padding="lg"
            title={
                <Text size="lg" fw={600}>
                    {fullName}
                </Text>
            }
        >
            <Stack gap="lg">
                {sectionEntries.map(({key, node}, index) => (
                    <Fragment key={key}>
                        {index > 0 && <Divider />}
                        {node}
                    </Fragment>
                ))}
            </Stack>
        </Modal>
    )
}
